import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowUpRightIcon,
  BugIcon,
  CheckIcon,
  ClockIcon,
  CopyIcon,
  InboxIcon,
  KeyRoundIcon,
  SparklesIcon,
  Trash2Icon,
} from "lucide-react";

import {
  useDeleteProject,
  useProject,
} from "../app/queries";
import { useProjectMetrics, type MetricsSummary } from "../hooks/useProjectMetrics";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EditProjectModal } from "../components/EditProjectModal";
import { ProjectTabs } from "../components/ProjectTabs";
import { Topbar } from "../components/Topbar";
import { ProjectNotFound } from "../components/ErrorState";
import { copyToClipboard } from "../lib/clipboard";
import { Button } from "../components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../components/ui/empty";
import { Separator } from "../components/ui/separator";
import { Skeleton } from "../components/ui/skeleton";
import { Page, PageBody, PageHeader, PageTitle, PageDescription } from "../components/PageLayout";
import {
  SeverityChip,
  StatusChip,
  formatExact,
  relativeTime,
  shortId,
} from "../lib/format";
import { cn } from "../lib/utils";
import type { Project, ReportSummary } from "../lib/api";

export function ProjectOverviewPage() {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const projects = useProject(id);
  const { metrics, recent } = useProjectMetrics(id);
  const remove = useDeleteProject({
    success: (_data, projectId) =>
      `Deleted project ${projects.project?.name ?? projectId}`,
    error: (err) =>
      err instanceof Error ? err.message : "Failed to delete project",
  });
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const project = projects.project;

  if (projects.isLoading) {
    return (
      <Page>
        <Topbar title="…" />
        <ProjectTabs />
        <PageBody width="wide">
          <Skeleton className="h-32 w-full rounded-xl" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-64 w-full rounded-xl" />
        </PageBody>
      </Page>
    );
  }

  if (!project) {
    return (
      <ProjectNotFound
        breadcrumb={[
          { label: "Projects", to: "/" },
          { label: "Not found" },
        ]}
      />
    );
  }

  return (
    <Page>
      <Topbar
        breadcrumb={[
          { label: "Projects", to: "/" },
          { label: project.name },
        ]}
      />
      <ProjectTabs />
      <ProjectHeader project={project} onEdit={() => setEditOpen(true)} />
      <PageBody width="wide">
        <DashboardSection
          projectId={id}
          metrics={metrics}
          recent={recent}
        />

        <DetailsSection
          project={project}
          metrics={metrics}
          onManageKeys={() => navigate(`/projects/${id}/api-keys`)}
        />

        <DangerZone onDelete={() => setDeleteOpen(true)} />
      </PageBody>

      <EditProjectModal
        project={project}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={() => {}}
      />
      <ConfirmDialog
        open={deleteOpen}
        title="Delete project"
        message={
          <>
            This permanently deletes <strong>{project.name}</strong>, its{" "}
            <strong>{metrics.total}</strong> reports, and all of its API keys
            and uploaded attachments. This action cannot be undone.
          </>
        }
        confirmLabel="Delete project"
        destructive
        busy={remove.isPending}
        requireText={project.key}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={async () => {
          try {
            await remove.mutateAsync(project.id);
            setDeleteOpen(false);
            navigate("/", { replace: true });
          } catch {
            // error toast is handled by useDeleteProject
          }
        }}
      />
    </Page>
  );
}

/* -------------------------------------------------------------------------- */
/* Header                                                                      */
/* -------------------------------------------------------------------------- */

function ProjectHeader({
  project,
  onEdit,
}: {
  project: Project;
  onEdit: () => void;
}) {
  return (
    <PageHeader>
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span
            className="grid size-12 shrink-0 place-items-center rounded-xl"
            style={{ background: project.color }}
            aria-hidden="true"
          >
            <BugIcon className="size-5 text-white/95" />
          </span>
          <div className="flex min-w-0 flex-col">
            <PageTitle>{project.name}</PageTitle>
            <p className="-mt-1 font-mono text-sm text-muted-foreground">
              {project.key}
            </p>
            {project.description ? (
              <PageDescription className="mt-2 max-w-2xl leading-relaxed">
                {project.description}
              </PageDescription>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={onEdit}>
            Edit project
          </Button>
        </div>
      </div>

      <dl className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <dt className="sr-only">Created</dt>
          <ClockIcon aria-hidden="true" className="size-3.5" />
          <dd title={formatExact(project.createdAt)}>
            Created {formatExact(project.createdAt)}
          </dd>
        </div>
        <Separator orientation="vertical" className="h-4" />
        <div className="flex items-center gap-2">
          <dt className="sr-only">Updated</dt>
          <SparklesIcon aria-hidden="true" className="size-3.5" />
          <dd title={formatExact(project.updatedAt)}>
            Updated {formatExact(project.updatedAt)}
          </dd>
        </div>
        <Separator orientation="vertical" className="h-4" />
        <div className="flex items-center gap-2">
          <dt className="sr-only">Project ID</dt>
          <dd className="font-mono">ID {shortId(project.id)}</dd>
        </div>
      </dl>
    </PageHeader>
  );
}

/* -------------------------------------------------------------------------- */
/* Dashboard                                                                   */
/* -------------------------------------------------------------------------- */

function DashboardSection({
  projectId,
  metrics,
  recent,
}: {
  projectId: string;
  metrics: MetricsSummary;
  recent: ReportSummary[];
}) {
  const navigate = useNavigate();
  const go = (query?: string) =>
    navigate(`/projects/${projectId}/reports${query ? `?${query}` : ""}`);

  const hasAnyReports = metrics.total > 0;

  return (
    <section className="flex flex-col gap-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-foreground">
            Dashboard
          </h2>
          <p className="text-sm text-muted-foreground">
            A snapshot of what your game is reporting right now.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Open critical"
          value={metrics.openCritical}
          helper={
            metrics.openCritical > 0
              ? "Requires attention"
              : "No critical issues"
          }
          tone={metrics.openCritical > 0 ? "danger" : "neutral"}
          onClick={() => go("severity=critical")}
        />
        <StatTile
          label="Reports"
          value={metrics.total}
          helper={
            metrics.recentCount > 0
              ? `${metrics.recentCount} in last 24h`
              : hasAnyReports
                ? "No recent reports"
                : "Awaiting first report"
          }
          onClick={() => go()}
        />
        <StatTile
          label="Active API keys"
          value={metrics.activeKeys}
          helper={
            metrics.totalKeys === 0
              ? "No keys minted"
              : `${metrics.totalKeys} total`
          }
          onClick={() => navigate(`/projects/${projectId}/api-keys`)}
        />
        <StatTile
          label="Last received"
          value={
            metrics.lastReceivedRelative ? (
              <span>{metrics.lastReceivedRelative}</span>
            ) : (
              <span className="text-muted-foreground">No reports yet</span>
            )
          }
          helper={
            metrics.lastReceivedExact
              ? formatExact(metrics.lastReceivedExact)
              : "Awaiting first report"
          }
          onClick={() => go()}
        />
      </div>

      <Card>
        <CardHeader>
          <div>
            <CardTitle>Recent activity</CardTitle>
            <CardDescription>
              {recent.length === 0
                ? "Latest reports submitted by your game."
                : `Latest ${recent.length} of ${metrics.total} reports.`}
            </CardDescription>
          </div>
          {recent.length > 0 ? (
            <CardAction>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => go()}
                className="text-muted-foreground"
              >
                View all
                <ArrowUpRightIcon aria-hidden="true" />
              </Button>
            </CardAction>
          ) : null}
        </CardHeader>
        <CardContent className="p-0">
          {recent.length === 0 ? (
            <div className="px-6 pb-6" data-testid="recent-activity">
              <EmptyNextStep />
            </div>
          ) : (
            <ul data-testid="recent-activity" className="flex flex-col">
              {recent.map((r, idx) => (
                <li
                  key={r.id}
                  className={cn(idx > 0 && "border-t border-border/60")}
                >
                  <Link
                    to={`/projects/${projectId}/reports/${r.id}`}
                    className="flex flex-col gap-2 px-6 py-4 transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none md:flex-row md:items-center md:gap-4"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <BugIcon
                        aria-hidden="true"
                        className="size-4 shrink-0 text-muted-foreground"
                      />
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium text-foreground">
                          {r.title || "Untitled report"}
                        </span>
                        <span className="font-mono text-xs text-muted-foreground">
                          {r.reference}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 md:justify-end">
                      <SeverityChip severity={r.severity} />
                      <StatusChip status={r.status} />
                      <span
                        className="text-xs text-muted-foreground md:w-28 md:text-right"
                        title={formatExact(r.receivedAt)}
                      >
                        {relativeTime(r.receivedAt)}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function StatTile({
  label,
  value,
  helper,
  tone = "neutral",
  onClick,
}: {
  label: string;
  value: React.ReactNode;
  helper?: string;
  tone?: "neutral" | "danger";
  onClick?: () => void;
}) {
  const interactive = !!onClick;
  const valueClass = cn(
    "text-3xl font-semibold leading-none tracking-tight tabular-nums",
    tone === "danger" ? "text-destructive" : "text-foreground",
  );
  if (!interactive) {
    return (
      <div className="flex flex-col gap-2 rounded-xl border bg-card p-4">
        <span className="text-sm font-medium text-muted-foreground">
          {label}
        </span>
        <span className={valueClass}>{value}</span>
        {helper ? (
          <span className="text-sm text-muted-foreground">{helper}</span>
        ) : null}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col gap-2 rounded-xl border bg-card p-4 text-left transition-colors hover:border-foreground/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
    >
      <span className="text-sm font-medium text-muted-foreground">
        {label}
      </span>
      <span className={valueClass}>{value}</span>
      {helper ? (
        <span className="text-sm text-muted-foreground">{helper}</span>
      ) : null}
    </button>
  );
}

function EmptyNextStep() {
  return (
    <Empty className="border-0 bg-transparent p-0">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <InboxIcon aria-hidden="true" />
        </EmptyMedia>
        <EmptyTitle>No reports yet</EmptyTitle>
        <EmptyDescription>
          Reports will appear here after your game sends its first submission.
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

/* -------------------------------------------------------------------------- */
/* Details / settings                                                          */
/* -------------------------------------------------------------------------- */

function DetailsSection({
  project,
  metrics,
  onManageKeys,
}: {
  project: Project;
  metrics: MetricsSummary;
  onManageKeys: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copyId() {
    const result = await copyToClipboard(project.id);
    if (!result.ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  return (
    <section className="flex flex-col gap-6">
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-foreground">
          Project details
        </h2>
        <p className="text-sm text-muted-foreground">
          Configuration and integration metadata for this project.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Project</CardTitle>
            <CardDescription>Identity and appearance.</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="flex flex-col gap-3 text-sm">
              <DetailRow label="Key" value={<span className="font-mono">{project.key}</span>} />
              <DetailRow
                label="Accent"
                value={
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="inline-block size-3 shrink-0 rounded-md ring-1 ring-inset ring-border"
                      style={{ background: project.color }}
                      aria-hidden="true"
                    />
                    <span className="text-foreground">{prettyColor(project.color)}</span>
                  </span>
                }
              />
              <DetailRow
                label="Created"
                value={
                  <span title={formatExact(project.createdAt)} className="whitespace-nowrap">
                    {formatExact(project.createdAt)}
                  </span>
                }
              />
              <DetailRow
                label="Updated"
                value={
                  <span title={formatExact(project.updatedAt)} className="whitespace-nowrap">
                    {formatExact(project.updatedAt)}
                  </span>
                }
              />
              <DetailRow
                label="ID"
                value={
                  <button
                    type="button"
                    onClick={copyId}
                    title={`Copy full project ID (${project.id})`}
                    className="inline-flex items-center gap-2 rounded-md px-1.5 py-0.5 -mx-1.5 font-mono text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
                  >
                    <span>{shortId(project.id)}</span>
                    {copied ? (
                      <CheckIcon aria-hidden="true" className="size-3 text-success" />
                    ) : (
                      <CopyIcon aria-hidden="true" className="size-3 text-muted-foreground" />
                    )}
                    <span className="sr-only">{copied ? "Copied" : "Copy project ID"}</span>
                  </button>
                }
              />
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>API keys</CardTitle>
              <CardDescription>
                Tokens your game uses to submit reports.
              </CardDescription>
            </div>
            {metrics.totalKeys > 0 ? (
              <CardAction>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onManageKeys}
                  className="text-muted-foreground"
                >
                  Manage keys
                  <ArrowUpRightIcon aria-hidden="true" />
                </Button>
              </CardAction>
            ) : null}
          </CardHeader>
          <CardContent>
            {metrics.totalKeys === 0 ? (
              <Empty className="border-0 bg-transparent p-0">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <KeyRoundIcon aria-hidden="true" />
                  </EmptyMedia>
                  <EmptyTitle>No API keys yet</EmptyTitle>
                  <EmptyDescription>
                    Mint your first key to start receiving reports from your game.
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <Button size="sm" onClick={onManageKeys} data-testid="overview-empty-mint-api-key">
                    <KeyRoundIcon aria-hidden="true" />
                    Mint API key
                  </Button>
                </EmptyContent>
              </Empty>
            ) : (
              <dl className="flex flex-col gap-3 text-sm">
                <DetailRow
                  label="Active"
                  value={
                    <span className="tabular-nums text-foreground">
                      {metrics.activeKeys}
                      <span className="text-muted-foreground"> of {metrics.totalKeys}</span>
                    </span>
                  }
                />
                <DetailRow
                  label="Last used"
                  value={
                    metrics.lastKeyUsedAt ? (
                      <span
                        title={formatExact(metrics.lastKeyUsedAt)}
                        className="whitespace-nowrap text-foreground"
                      >
                        {formatExact(metrics.lastKeyUsedAt)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Never used</span>
                    )
                  }
                />
                <DetailRow
                  label="Endpoint"
                  value={
                    <code className="rounded-md bg-muted px-2 py-0.5 font-mono text-xs text-foreground">
                      POST /api/v1/reports
                    </code>
                  }
                />
              </dl>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="truncate text-right">{value}</dd>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Danger zone                                                                 */
/* -------------------------------------------------------------------------- */

function DangerZone({ onDelete }: { onDelete: () => void }) {
  return (
    <section className="flex flex-col gap-2 rounded-lg border border-destructive/20 bg-destructive/[0.03] p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-semibold text-destructive">
            Danger zone
          </h2>
          <p className="text-sm text-muted-foreground">
            Deleting this project permanently removes its reports, API keys,
            and attachments. This action cannot be undone.
          </p>
        </div>
        <Button variant="destructive" size="sm" onClick={onDelete}>
          <Trash2Icon aria-hidden="true" />
          Delete project
        </Button>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

function prettyColor(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "Not set";
  if (trimmed.startsWith("var(")) return tokenLabel(trimmed) || trimmed;
  if (/^#[0-9a-f]{3,8}$/i.test(trimmed)) return trimmed.toUpperCase();
  return trimmed;
}

function tokenLabel(token: string): string | null {
  // Map CSS custom properties to readable labels for the project color field.
  const map: Record<string, string> = {
    "--success": "Success",
    "--warning": "Warning",
    "--info": "Info",
    "--destructive": "Critical",
    "--primary": "Primary",
    "--accent": "Accent",
    "--chart-1": "Chart 1",
    "--chart-2": "Chart 2",
    "--chart-3": "Chart 3",
    "--chart-4": "Chart 4",
    "--chart-5": "Chart 5",
  };
  const match = token.match(/var\((--[a-z0-9-]+)/i);
  if (!match) return null;
  return map[match[1]?.toLowerCase() ?? ""] ?? null;
}

/* -------------------------------------------------------------------------- */
/* Metrics                                                                     */
/* -------------------------------------------------------------------------- */

